import {
  get,
  parseRequestOptionsFromJSON,
  supported,
} from '@github/webauthn-json/browser-ponyfill';

export type ConditionalPasskeyLoginDeps = {
  supported: () => boolean;
  isConditionalMediationAvailable: () => Promise<boolean>;
  parseRequestOptionsFromJSON: typeof parseRequestOptionsFromJSON;
  get: typeof get;
  root?: ParentNode;
};

async function defaultConditionalMediationAvailable(): Promise<boolean> {
  if (
    typeof PublicKeyCredential === 'undefined' ||
    typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function'
  ) {
    return false;
  }
  return PublicKeyCredential.isConditionalMediationAvailable();
}

export async function startConditionalPasskeyLogin(
  deps: ConditionalPasskeyLoginDeps = {
    supported,
    isConditionalMediationAvailable: defaultConditionalMediationAvailable,
    parseRequestOptionsFromJSON,
    get,
  },
): Promise<void> {
  const root = deps.root ?? document;
  const formElement = root.querySelector<HTMLFormElement>(
    '[data-webauthn-conditional-form]',
  );
  const publicKeyInput = root.querySelector<HTMLInputElement>(
    '[data-webauthn-conditional-form] [data-public-key-field]',
  );
  if (!formElement || !publicKeyInput) {
    return;
  }
  if (!deps.supported()) {
    return;
  }
  if (!(await deps.isConditionalMediationAvailable())) {
    return;
  }

  try {
    const optionsString = String(formElement.dataset.webauthnOptions);
    const parsedOptions = deps.parseRequestOptionsFromJSON({
      publicKey: JSON.parse(optionsString),
      mediation: 'conditional',
    });
    const response = await deps.get(parsedOptions);
    publicKeyInput.value = JSON.stringify(response);
    formElement.submit();
  } catch {
    // User dismissed the prompt or the browser aborted; keep password login.
  }
}

void startConditionalPasskeyLogin();
